/**
 * QueuePreviewTable - Top 10 records sorted by conduct score for overview queues
 *
 * Simplified version of RecordsTable (no checkbox, no expand).
 * Cross-tag logic: only shows the "other" tag for the current queue.
 *
 * Atomic level: Organism
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../ui/table';
import { ConductScoreIndicator } from './ConductScoreIndicator';
import { Timestamp } from '../../../ui/timestamp';
import type { Record as ConductRecord } from '../../../../types/record';
import type { QueueType } from './QueueTabs';

const isVulnerable = (record: ConductRecord): boolean =>
  record.vulnerabilityDetection?.detected === true;

interface QueuePreviewTableProps {
  records: ConductRecord[];
  collectionId: string;
  activeQueue: QueueType;
  totalCount: number;
  className?: string;
}

const queueLabels: { [key in QueueType]: string } = {
  vulnerability: 'vulnerable customers',
  all: 'records',
};

const QueuePreviewTable: React.FC<QueuePreviewTableProps> = ({
  records,
  collectionId,
  activeQueue,
  totalCount,
  className = '',
}) => {
  const navigate = useNavigate();

  const handleRowClick = (record: ConductRecord) => {
    navigate(`/v4/records/${collectionId}/${record.id}`);
  };

  return (
    <div className={className}>
      <Table>
        <TableHeader>
          <TableRow className="border-border">
            <TableHead className="w-[80px] text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Score
            </TableHead>
            <TableHead className="w-[90px] text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Date
            </TableHead>
            <TableHead className="w-[140px] text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Customer
            </TableHead>
            <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Summary
            </TableHead>
            <TableHead className="w-[120px] text-right text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Tags
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.slice(0, 10).map((record) => (
            <TableRow
              key={record.id}
              className="cursor-pointer hover:bg-muted/50 border-border"
              onClick={() => handleRowClick(record)}
            >
              <TableCell>
                <ConductScoreIndicator
                  score={record.conductScore ?? 0}
                  size="small"
                />
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                <Timestamp value={record.date || record.createdAt} format="date-only" />
              </TableCell>
              <TableCell className="text-[13px] font-semibold truncate max-w-[140px]">
                {record.customerName || '—'}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground truncate max-w-[300px]">
                {record.summary || '—'}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex gap-1 justify-end">
                  {/* Cross-tags: show the "other" tag, not the queue's own tag */}
                  {activeQueue !== 'vulnerability' && isVulnerable(record) && (
                    <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-[rgba(88,0,149,0.08)] text-[#580095]">
                      Vulnerable
                    </span>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination line */}
      <div className="py-3 text-xs text-muted-foreground text-center">
        Showing top 10 of {totalCount.toLocaleString()} {queueLabels[activeQueue]} ·{' '}
        <Link
          to={`/v4/records/${collectionId}/records?queue=${activeQueue}`}
          className="text-primary font-semibold hover:underline"
        >
          View all →
        </Link>
      </div>
    </div>
  );
};

export { QueuePreviewTable };
export type { QueuePreviewTableProps };
